/**
 * IndexedDB Activity Repository Implementation
 *
 * Implements IActivityRepository using Dexie.js for IndexedDB storage.
 */

import { db } from '../../db';
import type { IActivityRepository } from '../interfaces/IActivityRepository';
import type { TicketActivity, ActivityType, ActivityVisibility } from '../../../types/ticketActivity';
import {
  ActivityType as ActivityTypeEnum,
  ActivityVisibility as ActivityVisibilityEnum,
  AuthorType,
} from '../../../types/ticketActivity';
import type { CreateActivityDTO, UpdateActivityDTO } from '../../../types/dto';

export class IndexedDBActivityRepository implements IActivityRepository {
  async getByTicketId(ticketId: string): Promise<TicketActivity[]> {
    return db.activities
      .where('ticketId')
      .equals(ticketId)
      .sortBy('createdAt');
  }

  async getByTicketIdAndVisibility(
    ticketId: string,
    visibility: ActivityVisibility
  ): Promise<TicketActivity[]> {
    const activities = await this.getByTicketId(ticketId);
    return activities.filter((a) => a.visibility === visibility);
  }

  async getByType(ticketId: string, type: ActivityType): Promise<TicketActivity[]> {
    const activities = await this.getByTicketId(ticketId);
    return activities.filter((a) => a.type === type);
  }

  async getLatest(ticketId: string): Promise<TicketActivity | null> {
    const activities = await db.activities
      .where('ticketId')
      .equals(ticketId)
      .reverse()
      .sortBy('createdAt');
    return activities[0] ?? null;
  }

  async getById(id: string): Promise<TicketActivity | null> {
    const activity = await db.activities.get(id);
    return activity ?? null;
  }

  async create(data: CreateActivityDTO): Promise<TicketActivity> {
    const activity: TicketActivity = {
      id: crypto.randomUUID(),
      ticketId: data.ticketId,
      type: data.type,
      visibility: data.visibility,
      authorId: data.authorId,
      authorName: data.authorName,
      authorType: data.authorType,
      content: data.content,
      previousStatus: data.previousStatus,
      newStatus: data.newStatus,
      waitingReason: data.waitingReason,
      waitingReasonNote: data.waitingReasonNote,
      previousPriority: data.previousPriority,
      newPriority: data.newPriority,
      previousAssignedTechId: data.previousAssignedTechId,
      previousAssignedTechName: data.previousAssignedTechName,
      newAssignedTechId: data.newAssignedTechId,
      newAssignedTechName: data.newAssignedTechName,
      previousTier: data.previousTier,
      newTier: data.newTier,
      previousGroup: data.previousGroup,
      newGroup: data.newGroup,
      escalationReason: data.escalationReason,
      attachments: data.attachments,
      displayMessage: data.displayMessage,
      triggersNotification: data.triggersNotification,
      questionText: data.questionText,
      questionActivityId: data.questionActivityId,
      isAnswered: data.isAnswered,
      createdAt: new Date(),
    };

    await db.activities.add(activity);
    return activity;
  }

  async update(id: string, data: UpdateActivityDTO): Promise<TicketActivity | null> {
    const existing = await this.getById(id);
    if (!existing) {
      return null;
    }

    // Preserve edit history
    const editHistory = existing.editHistory ?? [];
    if (existing.content && data.content && existing.content !== data.content) {
      editHistory.push({
        content: existing.content,
        editedAt: new Date(),
        editedBy: data.editedBy,
        editedByName: data.editedByName,
      });
    }

    const updatedActivity: TicketActivity = {
      ...existing,
      content: data.content ?? existing.content,
      editHistory,
    };

    await db.activities.put(updatedActivity);
    return updatedActivity;
  }

  async delete(id: string): Promise<boolean> {
    const existing = await this.getById(id);
    if (!existing) {
      return false;
    }
    await db.activities.delete(id);
    return true;
  }

  async getPublicActivities(ticketId: string): Promise<TicketActivity[]> {
    return this.getByTicketIdAndVisibility(ticketId, ActivityVisibilityEnum.PUBLIC);
  }

  async getInternalActivities(ticketId: string): Promise<TicketActivity[]> {
    return this.getByTicketIdAndVisibility(ticketId, ActivityVisibilityEnum.INTERNAL);
  }

  async getCommentCount(ticketId: string): Promise<number> {
    const activities = await this.getByTicketId(ticketId);
    return activities.filter(
      (a) => a.type === ActivityTypeEnum.COMMENT || a.type === ActivityTypeEnum.WORK_NOTE
    ).length;
  }

  async hasPublicITResponse(ticketId: string): Promise<boolean> {
    const publicActivities = await this.getPublicActivities(ticketId);
    return publicActivities.some(
      (a) =>
        a.type === ActivityTypeEnum.COMMENT &&
        a.authorType === AuthorType.IT_STAFF
    );
  }

  async hasUserResponseAfterWaiting(ticketId: string): Promise<boolean> {
    const activities = await this.getByTicketId(ticketId);

    // Find the last waiting status change
    let lastWaitingIndex = -1;
    for (let i = activities.length - 1; i >= 0; i--) {
      if (
        activities[i].type === ActivityTypeEnum.STATUS_CHANGE &&
        activities[i].newStatus === 'Waiting' &&
        activities[i].waitingReason === 'User'
      ) {
        lastWaitingIndex = i;
        break;
      }
    }

    // If no waiting status found, return true (no waiting to check)
    if (lastWaitingIndex === -1) {
      return true;
    }

    // Check if user responded after the waiting status
    for (let i = lastWaitingIndex + 1; i < activities.length; i++) {
      if (
        activities[i].type === ActivityTypeEnum.COMMENT &&
        activities[i].authorType === AuthorType.USER
      ) {
        return true;
      }
    }

    return false;
  }

  async getUnansweredQuestions(ticketId: string): Promise<TicketActivity[]> {
    const activities = await this.getByTicketId(ticketId);
    return activities.filter(
      (a) => a.type === ActivityTypeEnum.QUESTION_TO_USER && !a.isAnswered
    );
  }

  async markQuestionAnswered(questionId: string): Promise<TicketActivity | null> {
    const existing = await this.getById(questionId);
    if (!existing) {
      return null;
    }

    const updated: TicketActivity = {
      ...existing,
      isAnswered: true,
    };

    await db.activities.put(updated);
    return updated;
  }
}
